import api from '../utils/api'
import { API_ROOT } from '../constants'

/**
 * Games-specific facade over the generic `/v3/showcase` API (see backend
 * `modules/showcase/routes.js`) - every call is scoped to `type: 'game'` so a future showcase
 * (templates, themes, ...) can reuse the same backend without new endpoints. Keeps the Games UI
 * (GameCard/GameModal/GamesDirectory/CategoryPage) fully decoupled from that generalization.
 */
const SHOWCASE_TYPE = 'game'

/** List curated games. params: { category?, sort? ('new'|'likes'|'plays'), limit?, cursor?, search? } */
export const getGames = async (params = {}) => {
  const response = await api.get(`${API_ROOT}/v3/showcase`, { ...params, type: SHOWCASE_TYPE })
  return { ...response?.data, games: response?.data?.items || [] }
}

export const getGame = async (subdomain) => {
  const response = await api.get(`${API_ROOT}/v3/showcase/${subdomain}`)
  if (!response?.data) throw new Error('Game not found')
  return response.data
}

/**
 * Site owner submits their site to the game showcase for admin review. Requires auth. Lands in
 * `pending` - it does not go live until an admin approves it (see backend `modules/showcase/
 * services/showcase.js` `curateShowcaseItem`).
 *
 * Title/description/thumbnail aren't collected here - the backend reads those straight from the
 * site's own SEO fields (og title/description/image, set via the update-site modal's SEO
 * section), so the site must already have those filled in before it can be submitted.
 */
export const submitGameShowcase = async ({ subdomain, gameCategory, gameTags }) => {
  const response = await api.post(`${API_ROOT}/v3/showcase`, {
    subdomain,
    showcaseType: SHOWCASE_TYPE,
    showcaseCategory: gameCategory,
    showcaseTags: gameTags
  })
  return response?.data
}

/**
 * Whether a live-projects row should show the Submit Game CTA. Uses the site's stored classifier
 * category when the LLM tagged it as a Game.
 */
export const isGameShowcaseEligibleSite = (site) => {
  const category = site?.classification?.category || site?.linkRecord?.classification?.category
  if (!category) return false

  const normalized = String(category).toLowerCase().replace(/\s+/g, '_')
  return normalized === 'game' || normalized === 'games'
}

const isSelectableGameSite = (site) =>
  isGameShowcaseEligibleSite(site) &&
  site?.linkStatus !== 'error' &&
  !(site?.linkStatus === 'processing' && !site?.isPresignedUpload)

/**
 * Whether this site is eligible to submit to the game showcase (the LLM classifier tagged it as
 * a Game) and, if it's already been submitted, its current status ('pending'|'approved'|null).
 */
export const checkGameShowcaseEligibility = async (subdomain) => {
  const response = await api.get(`${API_ROOT}/v3/showcase/${subdomain}/eligibility`, { type: SHOWCASE_TYPE })
  return response?.data || { eligible: false, status: null }
}

/**
 * Fetches the current showcase status for every eligible game site, in parallel. Returns a map of
 * subdomain -> status ('pending'|'approved'|null - null meaning eligible but never submitted).
 * Best-effort per site - a failed lookup is treated as "never submitted" rather than failing the
 * whole batch, since this drives non-critical UI (submit CTA vs. status badge).
 */
export const getGameShowcaseStatuses = async (sites = []) => {
  const eligibleSites = sites.filter(isSelectableGameSite)

  const entries = await Promise.all(
    eligibleSites.map(async (site) => {
      try {
        const { status } = await checkGameShowcaseEligibility(site.subdomain)
        return [site.subdomain, status || null]
      } catch {
        return [site.subdomain, null]
      }
    })
  )

  return Object.fromEntries(entries)
}

/** Stable key for a live-sites list's eligible games, so callers can tell when it's actually
 * worth re-fetching statuses (e.g. a new game was created/deleted) rather than on every render. */
export const getEligibleGameSitesKey = (sites = []) =>
  sites
    .filter(isSelectableGameSite)
    .map((site) => site.subdomain)
    .sort()
    .join(',')

/**
 * Every eligible game site that hasn't been submitted yet (or was submitted and has since been
 * deleted from the showcase). Drives the "Submit Game" banner - if there's more than one, the
 * banner opens the picker step in GameShowcaseModal instead of assuming which game the user means.
 */
export const getUnsubmittedEligibleGameSites = (sites = [], statuses = {}) =>
  sites.filter((site) => isSelectableGameSite(site) && !statuses[site.subdomain])
